import { useSearchParams } from '@remix-run/react'
import { useState } from 'react'
import { Icon } from '#app/shared/ui/icons/Icon.js'
import { Button } from '#app/shared/ui/inputs/button/Button.tsx'
import { Checkbox } from '#app/shared/ui/inputs/Checkbox.js'
import { Input } from '#app/shared/ui/inputs/Input.js'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import { CarBrandEnum } from '../../../../../types/enums'

type FilterMarcaProps = {
  brandCounts: Record<string, number>
  isMobile?: boolean
}

export const FilterMarca = ({ brandCounts, isMobile }: FilterMarcaProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showAll, setShowAll] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const mainBrands = [
    CarBrandEnum.PORSCHE,
    CarBrandEnum.LAND_ROVER,
    CarBrandEnum.MERCEDES_BENZ,
    CarBrandEnum.MINI,
    CarBrandEnum.BMW,
    CarBrandEnum.AUDI,
    CarBrandEnum.LEXUS,
    CarBrandEnum.VOLVO,
    CarBrandEnum.VOLKSWAGEN,
    CarBrandEnum.ABARTH,
  ]

  const allBrandsList = Object.values(CarBrandEnum)

  const getBrandCount = (brand: string): number => brandCounts[brand] ?? 0

  const brandsWithCars = mainBrands.filter((brand) => getBrandCount(brand) > 0)

  const brandsWithoutCars = mainBrands.filter(
    (brand) => getBrandCount(brand) === 0,
  )

  const sortedMainBrandsWithCars = brandsWithCars.sort()
  const sortedMainBrandsWithoutCars = brandsWithoutCars.sort()

  const sortedBrands = [...allBrandsList].sort((a, b) => {
    const countA = getBrandCount(a)
    const countB = getBrandCount(b)

    if (countA === 0 && countB === 0) {
      return a.localeCompare(b)
    }
    if (countA === 0) return 1
    if (countB === 0) return -1
    return a.localeCompare(b)
  })

  const filteredBrands = sortedBrands.filter((brand) =>
    brand.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleCheckboxChange = (brand: string, checked: boolean) => {
    const currentFilters =
      searchParams.get('brands')?.split(',').filter(Boolean) || []
    let updatedFilters

    if (checked) {
      updatedFilters = [...new Set([...currentFilters, brand])]
    } else {
      updatedFilters = currentFilters.filter((f: string) => f !== brand)
    }

    const newSearchParams = new URLSearchParams(searchParams.toString())
    if (updatedFilters.length > 0) {
      newSearchParams.set('brands', updatedFilters.join(','))
    } else {
      newSearchParams.delete('brands')
    }
    setSearchParams(newSearchParams, { replace: true })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setShowAll(false)
  }

  const chunkArray = (array: string[], size: number) => {
    const chunks: string[][] = []
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size))
    }
    return chunks
  }

  const BrandCheckbox = ({
    brand,
    uniqueId,
  }: {
    brand: string
    uniqueId: string
  }) => {
    const count = getBrandCount(brand)
    const selectedBrands =
      searchParams.get('brands')?.split(',').filter(Boolean) || []
    const isChecked = selectedBrands.includes(brand)
    const isDisabled = count === 0

    return (
      <div
        className={`
          flex items-center gap-2 text-base-medium

          ${isDisabled ? 'text-gray-400' : 'text-neutral-700'}
          ${
            isMobile
              ? `
                w-full py-2

                last:border-b-0
              `
              : ''
          }
        `}
      >
        <Checkbox
          id={uniqueId}
          checked={isChecked}
          disabled={isDisabled}
          onCheckedChange={(checked) =>
            !isDisabled && handleCheckboxChange(brand, !!checked)
          }
        />
        <label htmlFor={uniqueId} className={isMobile ? 'flex-1' : ''}>
          {brand} {count > 0 && `(${count})`}
        </label>
      </div>
    )
  }

  return (
    <MainContainer>
      <div>
        <Input
          className="mb-7 mt-2 text-base-medium text-primary-700"
          leftIcon={
            <Icon
              name="basic/search"
              className="text-primary-500"
              size="16px"
            />
          }
          placeholder="Buscar Marca"
          onChange={handleInputChange}
        />

        {searchTerm ? (
          <>
            <p className="mb-5 mt-7 text-base-bold">
              Mostrando resultados para "{searchTerm}"
            </p>
            <div
              className={isMobile ? 'flex flex-col' : 'flex flex-wrap gap-5'}
            >
              {filteredBrands.length > 0 ? (
                filteredBrands.map((brand) => (
                  <BrandCheckbox
                    key={`search-${brand}`}
                    brand={brand}
                    uniqueId={`search-${brand}`}
                  />
                ))
              ) : (
                <p className="text-base-medium">
                  Parece que nada coincide con "{searchTerm}". Intenta con otro
                  término o encarga tu coche perfecto!
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="mb-5 mt-7 text-base-bold">Principales Marcas</p>
            <div
              className={isMobile ? 'flex flex-col' : 'flex flex-wrap gap-5'}
            >
              {sortedMainBrandsWithCars.map((brand) => (
                <BrandCheckbox
                  key={`main-${brand}`}
                  brand={brand}
                  uniqueId={`main-${brand}`}
                />
              ))}
              {sortedMainBrandsWithoutCars.map((brand) => (
                <BrandCheckbox
                  key={`main-${brand}`}
                  brand={brand}
                  uniqueId={`main-${brand}`}
                />
              ))}
            </div>
            {showAll && (
              <>
                <p className="mb-5 mt-7 text-base-bold">Todas las Marcas</p>
                {isMobile ? (
                  <div className="flex flex-col">
                    {sortedBrands.map((brand) => (
                      <BrandCheckbox
                        key={`all-${brand}`}
                        brand={brand}
                        uniqueId={`all-${brand}`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-x-[68px]">
                    {chunkArray(sortedBrands, 7).map((column, index) => (
                      <div
                        key={`column-${index}`}
                        className="flex flex-col gap-3"
                      >
                        {column.map((brand) => (
                          <BrandCheckbox
                            key={`all-${brand}`}
                            brand={brand}
                            uniqueId={`all-${brand}`}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <Button
              onClick={() => setShowAll(!showAll)}
              className={`
                mt-4 h-10 w-full border-neutral-100 bg-neutral-25 text-base-bold

                hover:border-neutral-100 hover:bg-neutral-75
                hover:text-base-bold
              `}
            >
              {showAll ? 'Ocultar' : 'Mostrar todas'}
            </Button>
          </>
        )}
      </div>
    </MainContainer>
  )
}
